import { App } from '@capacitor/app'
import { Network } from '@capacitor/network'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import {
  enqueueRun as libEnqueue,
  flushQueue as libFlush,
  listQueued as libList,
  type ExerciseRunPayload,
  type QueuedRun,
} from '@/lib/run-queue'

/**
 * Pinia wrapper around the offline run-queue lib. Owns the lifecycle
 * triggers (app boot, foreground resume, network back) and exposes the
 * `pending` count for any future "uploads pending..." indicator.
 *
 * The store is intentionally thin — the on-disk queue in
 * `@capacitor/preferences` is the source of truth, this store is just a
 * reactive view + the wiring that decides when to flush.
 *
 * One ambient subscriber is registered the first time {@link install}
 * is called. App.vue does this on mount.
 */
export const useRunQueueStore = defineStore('runQueue', () => {
  const pending = ref<number>(0)
  let installed = false

  async function refreshPending(): Promise<void> {
    const list: QueuedRun[] = await libList()
    pending.value = list.length
  }

  /**
   * Enqueue a run AND immediately refresh the pending counter. Same
   * contract as `lib/run-queue.ts:enqueueRun` (which also kicks off an
   * opportunistic flush) — this just adds the reactive bookkeeping.
   */
  async function enqueue(
    exerciseId: number,
    payload: ExerciseRunPayload,
    therapistPatientId?: string,
    homework: boolean = false,
    patientAuthored: boolean = false,
  ): Promise<void> {
    await libEnqueue(exerciseId, payload, therapistPatientId, homework, patientAuthored)
    await refreshPending()
  }

  async function flush(): Promise<void> {
    await libFlush()
    await refreshPending()
  }

  /**
   * Wire up the lifecycle subscribers. Idempotent — calling `install`
   * twice attaches one set of listeners.
   */
  async function install(): Promise<void> {
    if (installed) return
    installed = true
    await refreshPending()

    // Best-effort kick at install time so any leftovers from a previous
    // session upload as soon as the patient launches the app.
    void flush()

    void Network.addListener('networkStatusChange', (status) => {
      if (status.connected) {
        void flush()
      }
    })

    void App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        // Returning to the foreground — network may have flipped while
        // backgrounded without our listener firing on iOS.
        void flush()
      }
    })
  }

  return { pending, install, enqueue, flush, refreshPending }
})
